/**
 * useSpotlightRects — where the holes in the overlay go, frame by frame.
 *
 * The obvious implementation, a ResizeObserver, misses the case that matters
 * most: ReactFlow pans and zooms by transforming a parent, which changes a
 * node's screen position without resizing anything. So this measures on every
 * frame while the tutorial is active and only re-renders when a number actually
 * changed, which is cheap — four rect reads per frame at worst.
 */

import { useEffect, useRef, useState } from 'react';

const round = (n) => Math.round(n * 2) / 2;

function sameRects(a, b) {
    if (a === b) return true;
    if (!a || !b || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
        if (a[i].x !== b[i].x || a[i].y !== b[i].y || a[i].w !== b[i].w || a[i].h !== b[i].h) return false;
    }
    return true;
}

/**
 * @param {Array<{selector: string, el: Element}>|null} resolved
 * @param {boolean} enabled
 * @returns {{rects: Array<{x,y,w,h}>, anchorEl: Element|null}}
 */
export default function useSpotlightRects(resolved, enabled) {
    const [rects, setRects] = useState([]);
    const rectsRef = useRef([]);
    // Elements can be replaced by a re-render; keep the live ones here so the
    // tooltip anchors to whatever is on screen now.
    const elementsRef = useRef([]);
    const [anchorVersion, setAnchorVersion] = useState(0);

    useEffect(() => {
        elementsRef.current = resolved ? resolved.map((entry) => entry.el) : [];
        setAnchorVersion((v) => v + 1);
    }, [resolved]);

    useEffect(() => {
        if (!enabled || !resolved || resolved.length === 0) {
            rectsRef.current = [];
            setRects([]);
            return undefined;
        }

        let frameId = null;
        let stopped = false;

        const measure = () => {
            if (stopped) return;
            const next = [];
            let replaced = false;

            for (let i = 0; i < resolved.length; i += 1) {
                let el = elementsRef.current[i];
                if (!el || !el.isConnected) {
                    // The element was re-rendered out from under us. Re-query
                    // rather than dropping the hole.
                    const found = document.querySelector(resolved[i].selector);
                    if (found) {
                        elementsRef.current[i] = found;
                        el = found;
                        replaced = true;
                    }
                }
                if (!el || !el.isConnected) {
                    next.push({ x: 0, y: 0, w: 0, h: 0 });
                    continue;
                }
                const r = el.getBoundingClientRect();
                next.push({ x: round(r.left), y: round(r.top), w: round(r.width), h: round(r.height) });
            }

            if (replaced) setAnchorVersion((v) => v + 1);
            if (!sameRects(rectsRef.current, next)) {
                rectsRef.current = next;
                setRects(next);
            }
            frameId = requestAnimationFrame(measure);
        };

        frameId = requestAnimationFrame(measure);
        return () => {
            stopped = true;
            if (frameId !== null) cancelAnimationFrame(frameId);
        };
    }, [resolved, enabled]);

    return {
        rects,
        elements: elementsRef.current,
        anchorVersion,
    };
}
